import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Paper, Tooltip, Typography } from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';

type TMessages = {
  error: string;
  noData: string;
};

type TWidgetErrorProps = {
  isMaxHeight?: boolean;
  type?: keyof TMessages;
};

const WidgetError = ({ isMaxHeight = true, type = 'error' }: TWidgetErrorProps) => {
  const { t } = useTranslation();

  const text = useMemo(() => {
    const messages: TMessages = {
      error: t('blox.dashboards.errors.error'),
      noData: t('blox.dashboards.errors.noData'),
    };

    return messages[type] || messages.error;
  }, [t, type]);

  return (
    <Paper
      id="widget-error"
      data-testid="widget-error"
      elevation={0}
      sx={(theme) => {
        const isLightTheme = theme.palette.mode === 'light';

        const color = isLightTheme
          ? theme.blox?.colors?.medgray
          : theme.blox?.colors?.lightgray;

        const backgroundColor = isLightTheme
          ? theme.blox?.colors?.widgetGray
          : theme.blox?.colors?.darkgray;

        return {
          width: '100%',
          p: 1,

          maxHeight: isMaxHeight ? '100%' : 50,
          color,
          backgroundColor,
          overflow: 'hidden',
        };
      }}
    >
      {isMaxHeight ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <WarningAmberRoundedIcon fontSize="small" />
          <Typography variant="body2">{text}</Typography>
        </Box>
      ) : (
        <Tooltip
          title={text}
          disableInteractive
        >
          <Box
            sx={{
              maxHeight: 50 - 8, // 8px padding.
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <WarningAmberRoundedIcon fontSize="small" />
            <Typography variant="subtitle1">{text}</Typography>
          </Box>
        </Tooltip>
      )}
    </Paper>
  );
};

export default memo(WidgetError);
